import type { FC } from 'react';
import PropTypes from 'prop-types';
import { TextField, Card, CardContent, Grid, Typography, Box } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import MobileDatePicker from '@material-ui/lab/MobileDatePicker';
import type { DatePickerProps } from '@material-ui/lab/DatePicker';

interface DateFieldProps extends Omit<DatePickerProps<Date>, 'renderInput'> {
  error?: boolean;
  fullWidth: boolean;
  helperText?: any;
  label?: string;
  onChange: (date: Date) => void;
  placeholder?: string;
  value: Date | string | null;
  name?: string;
  format?: string;
  /** Horizontal alignment of the displayed date text (default 'left'). */
  align?: 'left' | 'center' | 'right';
}

export const DateField: FC<DateFieldProps> = (props) => {
  const {
    error,
    fullWidth,
    helperText,
    label,
    onChange,
    placeholder,
    value,
    name,
    align = 'left',
    ...other
  } = props;

  return (
    <Card
      elevation={8}
      sx={{
        backgroundColor: 'background.default',
        width: '100%'
      }}
    >
      <CardContent>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography
                color="textPrimary"
                variant="body1"
                fontWeight={'bold'}
              >
                {label}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs>
                <MobileDatePicker
                  inputFormat={'dd.MM.yyyy'}
                  openTo="year"
                  views={['year', 'month', 'day']}
                  onChange={onChange}
                  renderInput={({ InputProps, ...rest }) => (
                    <TextField
                      {...rest}
                      name={name}
                      error={error}
                      fullWidth={fullWidth}
                      helperText={helperText}
                      placeholder={placeholder}
                      multiline
                      variant="filled"
                      InputProps={{
                        disableUnderline: true,
                        ...InputProps
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiFilledInput-root': {
                          backgroundColor: 'background.paper',
                          borderRadius: 1,
                          px: 1.5,
                          py: 0.75,
                          transition: (theme) => theme.transitions.create([
                            'border-color',
                            'box-shadow'
                          ]),
                          '&:hover': {
                            backgroundColor: 'background.paper'
                          },
                          '&.Mui-focused': {
                            backgroundColor: 'background.paper',
                            boxShadow: (theme) => `${alpha(theme.palette.primary.main, 0.25)} 0 0 0 0.2rem`
                          },
                          '& .MuiFilledInput-input': {
                            fontSize: 14,
                            height: 'unset',
                            lineHeight: 1.6,
                            p: 0,
                            textAlign: align,
                          },
                          '&.Mui-disabled': {
                            backgroundColor: 'action.disabledBackground',
                            boxShadow: 'none',
                            borderColor: alpha('#D6DBE1', 0.5)
                          }
                        }
                      }}
                    />
                  )}
                  value={value}
                  {...other}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

DateField.propTypes = {
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
  helperText: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  value: PropTypes.instanceOf(Date)
};
